import { useEffect, useState } from "react";
import { type AcademicSnapshot, type FallbackTier, YALA } from "@yala/shared";
import { useTheme, type Theme } from "../hooks/useTheme";

/**
 * Three modes, cycled in a fixed order so the control is learnable: a single
 * button whose meaning is "next mode" rather than a hidden state machine.
 * The mark names the CURRENT mode, the label names the NEXT one — so a
 * first-time operator never has to guess what pressing it will do.
 *
 * The marks are plain geometric characters, not sun/moon pictographs: the
 * sun and moon signs render as colour emoji on Apple platforms, which put a
 * second hue in a one-hue instrument. Open = paper, filled = ink, half =
 * high contrast.
 */
const THEME_GLYPH: Record<Theme, string> = {
  light: "○",     // open = the default paper theme
  dark: "●",      // filled = night shift
  inverted: "◐",  // half-filled = high contrast
};
const NEXT_THEME: Record<Theme, string> = {
  light: "dark",
  dark: "inverted",
  inverted: "light",
};
import { useLang } from "../hooks/useLang";
import { formatDate } from "../lib/time";
import { APP_VERSION, APP_VERSION_LABEL } from "../lib/version";

interface FeedHealth {
  label: string;
  tier: FallbackTier | "loading";
  ageMinutes: number;
}

interface Props {
  feeds: FeedHealth[];
  onOpenCatalog: () => void;
  /** One click to the morning brief. */
  onOpenMayorsDesk: () => void;
  catalogCount: number;
  viewMode: "2D" | "3D" | "3DS";
  onCycleViewMode: () => void;
  onOpenManual: () => void;
  onOpenWhitepaper: () => void;
  onOpenAbout: () => void;
  onOpenSheets: () => void;
  onOpenAtlas: () => void;
  onOpenPlatform: () => void;
  onStartDemo: () => void;
  onFlip?: () => void;
  sheetsConfigured: boolean;
  academic: AcademicSnapshot | null;
  systemStatus?: "healthy" | "degraded" | "down" | "unknown";
}


const TEMPO_COLOR: Record<AcademicSnapshot["tempo"], string> = {
  low: "var(--text-3)",
  normal: "var(--data)",
  high: "var(--warn)",
  peak: "var(--bad)",
};

export function TopBar({ feeds, onOpenCatalog, onOpenMayorsDesk, catalogCount, viewMode, onCycleViewMode, onOpenManual, onOpenWhitepaper, onOpenAbout, onOpenSheets, onOpenAtlas, onOpenPlatform, onStartDemo, onFlip, sheetsConfigured, academic, systemStatus }: Props) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const { theme, cycle } = useTheme();
  const { lang, toggle: toggleLang } = useLang();
  const liveCount = feeds.filter((f) => f.tier === "live").length;

  // First-visit affordance: gently pulse the DEMO button until the user has
  // started the guided tour once, so a non-technical viewer (the Mayor) has an
  // obvious entry point into this dense command-centre view.
  const [demoHinted, setDemoHinted] = useState(() => {
    try { return window.localStorage.getItem("yala:demo-seen") !== "1"; } catch { return false; }
  });
  const startDemo = () => {
    try { window.localStorage.setItem("yala:demo-seen", "1"); } catch { /* private mode */ }
    setDemoHinted(false);
    onStartDemo();
  };

  return (
    <>
    <header className="topbar">
      {/* Sponsor + identity bar */}
      <div className="brand">
        {/* Municipal seal — principal identity mark */}
        <a className="sponsor sponsor-seal" href="https://www.yalacity.go.th" target="_blank"
           rel="noreferrer" aria-label="เทศบาลนครยะลา — Yala City Municipality">
          <img src="/logos/yala-seal.png" alt="Yala City Municipality seal" />
        </a>
        {/* Partners */}
        <a className="sponsor" href="https://www.depa.or.th" target="_blank"
           rel="noreferrer" aria-label="depa — Digital Economy Promotion Agency">
          <img src="/logos/depa.jpg" alt="depa" />
        </a>
        <a className="sponsor" href="https://www.smartcitythailand.or.th" target="_blank"
           rel="noreferrer" aria-label="Smart City Thailand">
          <img src="/logos/smart-city-thailand.jpg" alt="Smart City Thailand" />
        </a>
        <a className="sponsor sponsor-axiom" href="https://axiom.nonarkara.org" target="_blank"
           rel="noreferrer" aria-label="Axiom — Innovation as a Service">
          <img
            src={theme === "dark" ? "/logos/axiom-flat.svg" : "/logos/axiom.png"}
            alt="Axiom"
          />
        </a>
        <svg className="brand-wave" width="42" height="22" viewBox="0 0 42 22" aria-hidden>
          <path d="M0 11 Q 5 4, 10 11 T 20 11 T 30 11 T 42 11"
                fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.95"/>
          <path d="M0 17 Q 5 10, 10 17 T 20 17 T 30 17 T 42 17"
                fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.5"/>
        </svg>
        <div className="brand-stack">
          {/* Both forms are rendered and CSS picks one. The full name is the
              identity; the short form is what a 390px header can actually
              hold — the long one was being cut to "เทศบาลนครยะ…". */}
          <strong>
            <span className="brand-title-full">
              {lang === "th" ? YALA.name.th : YALA.name.en}
            </span>
            <span className="brand-title-short">
              {lang === "th" ? (YALA.shortName?.th ?? YALA.name.th) : (YALA.shortName?.en ?? YALA.name.en)}
            </span>
          </strong>
          <span className="brand-meta">
            <button
              type="button"
              className="brand-version"
              onClick={onOpenAbout}
              aria-label={lang === "th" ? `รุ่น ${APP_VERSION} เกี่ยวกับ งานวิจัย และชุดข้อมูล` : `Version ${APP_VERSION}. About, research, and data sets.`}
            >
              {APP_VERSION_LABEL}
            </button>
            <span className="brand-sub mono">
              YCM-01 · {lang === "th" ? "ชายแดนใต้" : "Deep South"}
            </span>
          </span>
        </div>
      </div>

      {/* Primary source health stays visible; secondary tools share one menu. */}

      <div className="topbar-right">
        <button
          onClick={onOpenMayorsDesk}
          className="mono mayor-desk-btn"
          aria-label={lang === "th" ? "เปิดโต๊ะนายก — สรุปเช้าวันนี้" : "Open the Mayor's Desk — this morning's brief"}
          title={lang === "th" ? "โต๊ะนายก — สรุปเช้าวันนี้" : "Mayor's Desk — this morning's brief"}
        >
          <span className="mayor-desk-btn__long">{lang === "th" ? "โต๊ะนายก" : "MAYOR'S DESK"}</span>
          <span className="mayor-desk-btn__short">{lang === "th" ? "โต๊ะนายก" : "DESK"}</span>
        </button>
        <button onClick={onOpenCatalog} className="mono catalog-toggle" aria-label="Open source catalog"
          title={`${liveCount}/${feeds.length} live feeds · ${catalogCount} catalog sources · ${systemStatus ?? "unknown"}`}>
          SOURCES · {liveCount}/{feeds.length}
        </button>
        <details className="work-menu" onKeyDown={(event) => {
          if (event.key === "Escape") { event.currentTarget.removeAttribute("open"); event.currentTarget.querySelector("summary")?.focus(); }
        }}>
          <summary className="mono" aria-label="Dashboard tools">{lang === "th" ? "เครื่องมือ" : "TOOLS"}</summary>
          <nav className="work-menu-items" aria-label="Dashboards, data catalogue and documentation"
                onClick={(event) => {
                  if ((event.target as HTMLElement).closest("button")) event.currentTarget.parentElement?.removeAttribute("open");
                }}>
            <button
              onClick={onOpenSheets}
              className={`mono sheets-btn ${sheetsConfigured ? "sheets-btn-live" : ""}`}
              aria-label={sheetsConfigured ? "Open Google Sheets live feed" : "Set up Google Sheets integration"}
              title={sheetsConfigured ? "Open the live-data Google Sheet" : "Connect a Google Sheet to this dashboard"}
            >
              {sheetsConfigured ? "▦ SHEETS" : "▦ SHEETS"}
            </button>
            <button
              onClick={onOpenAtlas}
              className="atlas-toggle"
              aria-label="Open the Yala Data Atlas — outcome indicators, charts, and the full data-source catalog"
              title="Data Atlas — poverty, education, health, climate, economy outcomes + 200+ sources"
            >
              ◷ ATLAS
            </button>
            <button
              onClick={onOpenPlatform}
              className="kp-toggle"
              aria-label="Open the Yala Knowledge Platform — search, academy, AI concierge, archive"
              title="Knowledge Platform — search · learn · ask AI · time-machine archive"
            >
              ⌕ LEARN
            </button>
            {onFlip ? (
              <button
                onClick={onFlip}
                className="flip-toggle"
                aria-label="Flip to the Watch Terminal — Bloomberg-style real-time + reference data"
                title="Flip to the Watch Terminal (System B)"
              >
                ⇄ TERMINAL
              </button>
            ) : null}
            <button
              onClick={onOpenWhitepaper}
              className="mono"
              aria-label="Open whitepaper — platform overview and usage manual (TH/EN)"
              title="Whitepaper — platform overview · Thai + English"
            >
              WP
            </button>
            <button
              onClick={onOpenManual}
              className="mono manual-trigger"
              aria-label="Open user manual"
              title="Manual — every button, color, acronym"
            >
              ?
            </button>
          </nav>
        </details>
        <button
          onClick={startDemo}
          className={`demo-start-btn ${demoHinted ? "demo-start-btn--hint" : ""}`}
          aria-label={lang === "th" ? "เริ่มการนำเสนออัตโนมัติ" : "Start the guided demo tour"}
          title={lang === "th" ? "นำเสนออัตโนมัติ" : "Guided demo tour"}
        >
          ▶ <span className="demo-start-btn__label">{lang === "th" ? "นำเสนอ" : "DEMO"}</span>
        </button>
        <button
          type="button"
          onClick={onOpenAbout}
          className="mono about-toggle"
          aria-label={lang === "th" ? "เกี่ยวกับรุ่นนี้ งานวิจัย และชุดข้อมูล" : "About this version, research, and data sets"}
          title={lang === "th" ? "เกี่ยวกับ · งานวิจัย · ชุดข้อมูล" : "About · research · data sets"}
        >
          ABOUT
        </button>
        <button
          onClick={onCycleViewMode}
          className={`mono dim-toggle vm-${viewMode}`}
          aria-label={`View mode: ${viewMode}. Click to cycle 2D → 3D → 3D substructure`}
          title={
            viewMode === "2D"
              ? "Tap → 3D · extrude buildings"
              : viewMode === "3D"
                ? "Tap → 3DS · transparent superstructure, show underground"
                : "Tap → 2D · top-down"
          }
        >
          {viewMode}
        </button>
        <button
          onClick={toggleLang}
          className="mono lang-toggle"
          aria-label={lang === "th" ? "เปลี่ยนเป็นภาษาอังกฤษ — switch to English" : "Switch to Thai — เปลี่ยนเป็นภาษาไทย"}
          title={lang === "th" ? "English" : "ภาษาไทย"}
        >
          {lang === "th" ? "TH" : "EN"}
        </button>
        <button
          onClick={cycle}
          className="theme-toggle"
          aria-label={`Switch to ${NEXT_THEME[theme]} theme`}
          title={`${NEXT_THEME[theme]} theme`}
        >
          {THEME_GLYPH[theme]}
        </button>
      </div>

      <div className="clock">
        {academic?.current && (
          <span
            className="mono academic-chip"
            style={{ color: TEMPO_COLOR[academic.tempo] }}
            title={`${academic.current.label} · ${academic.current.describe}${
              academic.next && academic.daysToNext != null
                ? ` · next: ${academic.next.label} in ${academic.daysToNext}d`
                : ""
            }`}
          >
            {academic.current.label.toUpperCase()}
          </span>
        )}
        <span className="clock-date">{formatDate(now)}</span>
        <span className="clock-time">
          {now.toLocaleTimeString("en-GB", { hour12: false })}
        </span>
      </div>
    </header>

    </>
  );
}
